import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { fromEvent } from 'rxjs';
import { WaResizeObserver } from '@ng-web-apis/resize-observer';
import { ScaleRectPipe } from '../page-overlay/overlay-rect/scale-rect.pipe';
import { RectDisplayStylePipe } from '../page-overlay/overlay-rect/rect-display-style.pipe';
import { Dimension2d } from '../page-overlay/model';
import { OverlayDimensionService } from '../page-overlay/overlay-dimension.service';
import { OcrSearchResultToWordRectPipe } from './ocr-search-result-to-word-rect.pipe';
import { SearchResultNavigator } from '../service/search-result-navigator';

// A smooth scroll animates through every page in between, which keeps intersection observers
// and image loading busy over the whole distance. Beyond a few screens that costs seconds.
const SMOOTH_SCROLL_MAX_SCREENS = 3;

const scrollBehaviourFor = (target: HTMLElement): ScrollBehavior =>
  Math.abs(target.getBoundingClientRect().top) > SMOOTH_SCROLL_MAX_SCREENS * window.innerHeight
    ? 'instant'
    : 'smooth';

@Component({
  selector: 'shared-dp-search-result-overlay',
  templateUrl: './search-result-overlay.component.html',
  styleUrl: './search-result-overlay.component.scss',
  imports: [
    ScaleRectPipe,
    NgStyle,
    OcrSearchResultToWordRectPipe,
    NgClass,
    ScaleRectPipe,
    RectDisplayStylePipe,
    WaResizeObserver,
    OcrSearchResultToWordRectPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultOverlayComponent {
  readonly pageId = input.required<string>();

  protected readonly pingedWord = computed(() => this.searchResultNavigator.pingedWord());

  protected readonly foundWords = computed(
    () => this.searchResultNavigator.foundWords()[this.pageId()],
  );

  protected readonly dimension = computed<Dimension2d | undefined>(
    () => this.dimensionService.dimensionsByPageId()[this.pageId()],
  );

  private readonly searchResultNavigator = inject(SearchResultNavigator);
  private readonly dimensionService = inject(OverlayDimensionService);

  private readonly pingElement = viewChild<ElementRef<HTMLElement>>('pingElement');

  private readonly hostElement = inject(ElementRef);

  constructor() {
    effect(() => {
      const ping = this.pingElement();
      if (ping === undefined) return;
      fromEvent(ping.nativeElement, 'animationend', () =>
        ping.nativeElement.classList.remove('ping-box'),
      ).subscribe();
    });

    effect(() => {
      const ping = this.pingElement();
      const pingedWord = this.pingedWord();

      if (!ping || pingedWord?.pageId !== this.pageId()) return;

      ping.nativeElement.classList.remove('ping-box');
      // force a browser reflow which in turn forces the animation to restart
      ping.nativeElement.getBoundingClientRect();
      ping.nativeElement.classList.add('ping-box');

      setTimeout(() =>
        ping.nativeElement.scrollIntoView({
          block: 'center',
          behavior: scrollBehaviourFor(ping.nativeElement),
        }),
      );
    });
  }

  protected updateDimensions(): void {
    this.dimensionService.setDimension(this.pageId(), {
      width: this.hostElement.nativeElement.offsetWidth,
      height: this.hostElement.nativeElement.offsetHeight,
    });
  }
}
